import { useEffect } from "react";

interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path?: string;
  type?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const canonicalUrl = (path: string) => {
  if (typeof window === "undefined") return path;
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const prefix = basePath ? `/${basePath}` : "";
  return `${window.location.origin}${prefix}${path === "/" ? "/" : path}`;
};

export default function useSeo({
  title,
  description,
  keywords,
  path = "/",
  type = "website",
  jsonLd,
}: SeoOptions) {
  useEffect(() => {
    const url = canonicalUrl(path);
    document.title = title;
    upsertMeta("name", "description", description);
    if (keywords) {
      upsertMeta("name", "keywords", keywords);
    }
    upsertMeta("name", "robots", "index,follow");
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", "17爆料网");
    upsertMeta("property", "og:locale", "zh_CN");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
  }, [title, description, keywords, path, type]);

  useEffect(() => {
    const id = "seo-jsonld";
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    if (!jsonLd) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [jsonLd]);
}